/**
 * Loads a training track's playbook into salesPlaybookSlice so the live-call
 * SalesPlaybookPanel can render it, exactly as it does on a call.
 *
 * The panel reads entirely from Redux; without this it mounts empty, which
 * looks like a broken panel rather than an unconfigured track. The playbook
 * session state is cleared on mount and unmount so a call's ticks never leak
 * into training, or training's into a call.
 *
 * Returns true once the track's playbook is in the store.
 */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { apiService } from '../services/apiService';
import type { AppDispatch } from '../store';
import {
  clearSalesPlaybookSession,
  fetchPlaybooks,
  fetchRoleData,
  setSelectedPlaybook,
  setSelectedRole,
} from '../store/slices/salesPlaybookSlice';

interface TrackPlaybookConfig {
  enabled?: boolean;
  /** The trainingtracks wire key; its VALUE is the playbookName. */
  industry?: string;
  roleName?: string;
}

interface TrackPlaybookFields {
  playbookConfig?: TrackPlaybookConfig;
}

export function useTrainingTrackPlaybook(trackId: string | null): boolean {
  const dispatch = useDispatch<AppDispatch>();
  const [hasPlaybook, setHasPlaybook] = useState(false);

  useEffect(() => {
    dispatch(clearSalesPlaybookSession());
    setHasPlaybook(false);
    if (!trackId) return () => { dispatch(clearSalesPlaybookSession()); };
    let isCancelled = false;

    const loadPlaybook = async (): Promise<void> => {
      const response = await apiService.get<TrackPlaybookFields>(`/training/tracks/${trackId}`);
      const playbookConfig = response?.data?.playbookConfig;
      const playbookName = playbookConfig?.industry;
      const roleName = playbookConfig?.roleName;
      if (isCancelled || !playbookConfig?.enabled || !playbookName || !roleName) return;

      const playbooks = (await dispatch(fetchPlaybooks()).unwrap()) ?? [];
      const playbook = playbooks.find((entry) => entry?.playbookName === playbookName);
      if (isCancelled || !playbook) return;

      dispatch(setSelectedPlaybook(playbook));
      const role = (playbook?.roles ?? []).find((entry) => entry?.name === roleName);
      if (role) dispatch(setSelectedRole(role));
      void dispatch(fetchRoleData({ playbookName, roleName }));
      setHasPlaybook(true);
    };

    loadPlaybook().catch((loadError) => {
      // Not a session failure: the session runs without the reference panel.
      console.warn('useTrainingTrackPlaybook: could not load the track playbook', loadError);
    });

    return () => {
      isCancelled = true;
      dispatch(clearSalesPlaybookSession());
    };
  }, [dispatch, trackId]);

  return hasPlaybook;
}
